import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Phone, MessageCircle, MapPin, CheckCircle, Building2, Warehouse, Factory, Store, Instagram, Menu, X, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaPosObraBH = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Gostaria de solicitar um orçamento de limpeza pós-obra em Belo Horizonte.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  const diferenciais = [
    'Atendimento em toda Belo Horizonte',
    'Equipamentos profissionais',
    'Equipe técnica especializada',
    'Limpeza de grandes áreas',
    'Atendimento para empresas e condomínios',
    'Orçamento sem compromisso'
  ];

  const tiposImoveis = [
    { title: 'Obras Comerciais', desc: 'Lojas, escritórios, clínicas e estabelecimentos comerciais em BH' },
    { title: 'Obras Industriais', desc: 'Galpões, fábricas e áreas industriais na região de BH' },
    { title: 'Condomínios', desc: 'Áreas comuns, garagens e halls de condomínios em Belo Horizonte' },
    { title: 'Empreendimentos', desc: 'Construtoras e incorporadoras que entregam obras em BH' }
  ];

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/limpeza-pos-obra-belo-horizonte'].title}
        description={seoData['/limpeza-pos-obra-belo-horizonte'].description}
        canonical={seoData['/limpeza-pos-obra-belo-horizonte'].canonical}
      /> bg-white">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="text-green-400 font-semibold">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Locação</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Blog</button>
              <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors flex items-center">
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </a>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-slate-800 to-slate-900 text-white py-24 md:py-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(30, 41, 59, 0.88)), url('https://images.unsplash.com/photo-1581244277943-fe4a9c777189?w=1600')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              BELO HORIZONTE
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Limpeza Pós-Obra em Belo Horizonte
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Equipamentos profissionais e equipe especializada para entregar seu imóvel pronto para uso
            </p>

            <Button onClick={handleWhatsAppClick} size="lg" className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all">
              <MessageCircle className="w-5 h-5 mr-2" />
              Solicitar Orçamento em BH
            </Button>
          </div>
        </div>
      </section>

      {/* Sobre BH */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Limpeza Pós-Obra Profissional em Belo Horizonte
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Belo Horizonte possui um mercado imobiliário ativo com constante entrega de obras comerciais, industriais e residenciais. A WellTec atende construtoras, incorporadoras, empresas e condomínios que precisam entregar o imóvel limpo e pronto para uso.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              Utilizamos <strong>equipamentos profissionais de limpeza</strong> como lavadoras de piso, varredeiras industriais e equipamentos especializados que garantem produtividade e resultado superior à limpeza convencional.
            </p>

            <Card className="bg-blue-50 border-2 border-blue-200 mt-8">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-8 h-8 text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Atendimento em toda Belo Horizonte</h3>
                    <p className="text-gray-700">
                      Centro, Savassi, Pampulha, Barreiro, Venda Nova e todas as regionais de BH
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tipos de Imóveis */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Atendemos Diversos Tipos de Obras em BH
            </h2>
          </div>

          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
            {tiposImoveis.map((tipo) => (
              <Card key={tipo.title} className="border-2 border-gray-100 hover:border-green-500 transition-all duration-300">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{tipo.title}</h3>
                  <p className="text-gray-600">{tipo.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Por que escolher a WellTec em Belo Horizonte?
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {diferenciais.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-medium">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <Button onClick={handleWhatsAppClick} size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8 py-6">
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento em Belo Horizonte
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Precisa de Limpeza Pós-Obra em Belo Horizonte?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Entre em contato e receba uma avaliação para o seu imóvel
          </p>
          <Button onClick={handleWhatsAppClick} size="lg" className="bg-green-600 hover:bg-green-700 text-white px-10 py-6">
            <MessageCircle className="w-5 h-5 mr-2" />
            Falar com a WellTec
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em Belo Horizonte e região</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
                <li><button onClick={handleWhatsAppClick} className="hover:text-green-400">WhatsApp</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Botão Flutuante WhatsApp */}
      <button onClick={handleWhatsAppClick} className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50" aria-label="WhatsApp">
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LimpezaPosObraBH;